import { api, jsonBody } from './api'

export interface MediaAsset {
  id: string
  name: string
  kind: 'IMAGE' | 'VIDEO'
  status: string
  sizeBytes: number
  width: number | null
  height: number | null
  durationMs: number | null
  thumbnailUrl: string | null
  createdAt: string
}

interface MultipartUpload {
  assetId: string
  uploadId: string
  partSizeBytes: number
  parts: { partNumber: number; url: string }[]
}

export type UploadStatus =
  'READY' | 'HASHING' | 'UPLOADING' | 'PROCESSING' | 'DONE' | 'ERROR'

async function sha256(file: File): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer())
  return Array.from(new Uint8Array(digest))
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('')
}

export async function uploadMediaFile(
  file: File,
  onProgress: (percent: number, status: UploadStatus) => void = () => undefined
): Promise<string> {
  onProgress(2, 'HASHING')
  const checksumSha256 = await sha256(file)
  onProgress(5, 'UPLOADING')
  const upload = await api<MultipartUpload>('/media/uploads', {
    method: 'POST',
    body: jsonBody({
      fileName: file.name,
      contentType: file.type,
      sizeBytes: file.size,
      checksumSha256,
    }),
  })
  const completed: { partNumber: number; etag: string }[] = []
  for (const [index, part] of upload.parts.entries()) {
    const start = index * upload.partSizeBytes
    const end = Math.min(file.size, start + upload.partSizeBytes)
    const response = await fetch(part.url, {
      method: 'PUT',
      body: file.slice(start, end),
    })
    if (!response.ok) {
      throw new Error(`Upload part ${part.partNumber} failed`)
    }
    const etag = response.headers.get('ETag')
    if (!etag) {
      throw new Error(
        'Object storage did not expose ETag; check the MinIO CORS policy.'
      )
    }
    completed.push({ partNumber: part.partNumber, etag })
    onProgress(
      Math.round(5 + ((index + 1) / upload.parts.length) * 90),
      'UPLOADING'
    )
  }
  onProgress(97, 'PROCESSING')
  await api(`/media/uploads/${upload.assetId}/complete`, {
    method: 'POST',
    body: jsonBody({ uploadId: upload.uploadId, parts: completed }),
  })
  onProgress(100, 'DONE')
  return upload.assetId
}
